import { useState, type ChangeEvent } from 'react';
import type { Squad, SquadPlayer, SquadValidation } from '@fantasy/types';
import { api, ApiRequestError, type ScreenshotImportResponse, type UnmatchedScreenshotPlayer } from '../api.ts';

const ALLOWED_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);

/** Starting XI is grouped and displayed in this order, regardless of the
 * order players come back from matching. */
const POSITION_ORDER = ['GKP', 'DEF', 'MID', 'FWD'] as const;

interface ScreenshotImportProps {
  gameweek: number;
  onImported: (squad: Squad, validation: SquadValidation) => void;
  /** Handed a partial import's structured result — the matched players
   * (position known, so they can go straight into their real slot) and the
   * unmatched name fragments (position unknown — nothing to slot them into
   * automatically) — so the caller can build a squad editor pre-filled with
   * everything the import got right instead of leaving the user to rebuild
   * all fifteen from scratch. */
  onPartialImport: (matched: SquadPlayer[], unmatched: UnmatchedScreenshotPlayer[]) => void;
}

type Status = 'idle' | 'reading' | 'ready' | 'confirming';

/**
 * Uploads a "Pick Team" screenshot, extracts the squad, and — once every
 * player is matched — saves it exactly the way the paste flow does. A
 * screenshot has no prices on it, so the saved squad's prices are today's
 * prices from our own data, not necessarily what the manager actually paid.
 *
 * One of two forms `ImportSquad` switches between (the other is
 * `EntryIdImport`) — this component owns just the form/preview content, not
 * the accordion shell around it.
 */
export function ScreenshotImport({ gameweek, onImported, onPartialImport }: ScreenshotImportProps) {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScreenshotImportResponse | null>(null);

  async function onFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setError(null);
    setResult(null);

    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Upload a PNG, JPEG or WebP screenshot.');
      return;
    }

    setStatus('reading');
    try {
      const image = await readAsBase64(file);
      const response = await api.importScreenshot({
        image,
        mediaType: file.type as 'image/png' | 'image/jpeg' | 'image/webp',
        gameweek,
      });
      setResult(response);
      if (response.unmatched.length > 0) {
        onPartialImport(response.matched, response.unmatched);
      }
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not read that screenshot.');
    } finally {
      setStatus('idle');
    }
  }

  async function onConfirm() {
    if (!result?.squad) return;

    setStatus('confirming');
    setError(null);
    try {
      const created = await api.createSquad(result.squad);
      onImported(created.squad, created.validation);
      setResult(null);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : 'Could not save that squad.');
    } finally {
      setStatus('idle');
    }
  }

  /** The preview's other way out: nothing is saved yet, so discarding is just
   * clearing local state — no squad was ever created to undo. Lets a user who
   * spots a wrong-but-plausible match (same-club namesake, etc.) back out and
   * try a different screenshot instead of confirming something they didn't
   * mean to, or picking a new file just to notice the old preview vanished. */
  function onDiscard() {
    setResult(null);
    setError(null);
  }

  return (
    <div className="mt-md">
      <p className="hint">
        Upload your "Pick Team" screen. It shows no prices, so the imported squad uses today's
        prices from our own data — not necessarily what you actually paid for each player.
      </p>

      <label htmlFor="screenshot-file">Screenshot</label>
      <input
        id="screenshot-file"
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={onFileSelected}
        disabled={status === 'reading' || status === 'confirming'}
      />

      {status === 'reading' && (
        <p className="hint">
          <span className="spinner" aria-hidden="true" />
          Reading the screenshot…
        </p>
      )}

      {result && result.unmatched.length > 0 && (
        <UnmatchedList players={result.unmatched} matchedCount={result.matched.length} />
      )}

      {error && (
        <ul className="notices mt-xl">
          <li className="error">{error}</li>
        </ul>
      )}

      {result?.squad && (
        <div className="mt-xl">
          <p className="hint">All {result.squad.players.length} players matched — ready to import.</p>
          <SquadPreview players={result.squad.players} />
          <div className="row">
            <button
              type="button"
              onClick={onConfirm}
              disabled={status === 'confirming'}
              aria-busy={status === 'confirming'}
            >
              {status === 'confirming' && <span className="spinner" aria-hidden="true" />}
              {status === 'confirming' ? 'Importing…' : 'Confirm import'}
            </button>
            <button type="button" className="ghost" onClick={onDiscard} disabled={status === 'confirming'}>
              Discard
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** The extracted roster, shown so the user can catch a wrong-but-plausible
 * match (e.g. a same-club namesake) before committing to the import —
 * starting XI grouped by position, then bench in bench order. */
function SquadPreview({ players }: { players: SquadPlayer[] }) {
  const bench = players
    .filter((player) => !player.isStarting)
    .sort((a, b) => (a.benchOrder ?? 0) - (b.benchOrder ?? 0));

  return (
    <div className="pitch mt-xl">
      {POSITION_ORDER.map((position) => {
        const row = players.filter((player) => player.isStarting && player.position === position);
        if (row.length === 0) return null;

        return (
          <div key={position} className="pitch-row">
            {row.map((player) => (
              <PlayerCard key={player.name} player={player} />
            ))}
          </div>
        );
      })}

      <div className="dugout">
        <p className="dugout-label">Bench</p>
        <div className="dugout-row">
          {bench.map((player) => (
            <PlayerCard key={player.name} player={player} />
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayerCard({ player }: { player: SquadPlayer }) {
  return (
    <div className="pitch-card" data-club={player.club}>
      <div className="name">
        {player.name}
        {player.isCaptain && <span className="pill pill--captain">C</span>}
        {player.isViceCaptain && <span className="pill pill--vice">V</span>}
      </div>
      <div className="meta">{player.club}</div>
    </div>
  );
}

/** The itemized "Couldn't match: X (Y)" list lives in the editor's own banner
 * now (see App.tsx) — this stays a summary line only, so the two don't render
 * the same text twice. */
function UnmatchedList({ players, matchedCount }: { players: UnmatchedScreenshotPlayer[]; matchedCount: number }) {
  const lineWord = players.length === 1 ? 'slot' : 'slots';

  return (
    <div className="mt-xl">
      <p className="hint">
        {matchedCount > 0
          ? `${matchedCount} correctly matched player${matchedCount === 1 ? '' : 's'} carried into the editor below — fill the flagged ${lineWord} before analysing.`
          : `None of these could be matched confidently — fill the flagged ${lineWord} in the editor below before analysing.`}
      </p>
    </div>
  );
}

/** Reads a File as base64 without the `data:image/...;base64,` prefix — that
 * prefix is a browser convention the API's request schema doesn't expect. */
function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });
}
